import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useContext, useMemo } from 'react';
import ThemeContext from '../../providers/ThemeProvider';

const SelectedFilters = ({
  activeFilters = [],
  onRemoveFilter,
  onClearAll,
  className = '',
}) => {
  const theme = useContext(ThemeContext);

  const sortedFilters = useMemo(() => {
    return [...activeFilters].sort((a, b) => {
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }

      return a.label.localeCompare(b.label);
    });
  }, [activeFilters]);

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className={`selected-filters-display ${className}`}>
      <div className="selected-filters">
        {sortedFilters?.map((filter, index) => (
          <div
            className="filter-chip"
            key={`${filter.category}-${filter.value}-${index}`}
          >
            <span className="filter-label">{filter.label}</span>
            <button
              aria-label={`Remove ${filter.label} filter`}
              className="remove-btn"
              onClick={() => onRemoveFilter(filter.category, filter.value)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
        <span className="clear-all-btn" onClick={onClearAll}>
          Clear All
        </span>
      </div>

      <style jsx>{`
        .selected-filters-display {
          border-radius: 8px;
          padding-left: 10px;
          padding-right: 10px;
        }

        .selected-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          flex-direction: row;
        }

        .filter-chip {
          display: inline-flex;
          align-items: center;
          background: white;
          color: ${theme.colors.primary};
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          border: 1px solid ${theme.colors.primary};
          transition: all 0.2s ease;
          gap: 8px;
        }

        .filter-chip:hover {
          background: ${theme.colors.primary}10;
        }

        .remove-btn {
          background: none;
          border: none;
          color: ${theme.colors.primary};
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          transition: all 0.2s;
          padding: 0;
        }

        .remove-btn:hover {
          background: ${theme.colors.primary};
          transform: scale(1.1);
        }

        .clear-all-btn {
          background: none;
          text-decoration: underline;
          color: ${theme.colors.primary};
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
        }

        .clear-all-btn:hover {
          background: ${theme.colors.primary};
          color: white;
          transform: translateY(-1px);
        }

        @media (max-width: ${theme.breakpoints.mdUp}) {
          .selected-filters-display {
            margin-bottom: 4px;
            border: none;
          }

          .filter-chip {
            font-size: 14px;
            padding: 4px 8px;
            gap: 6px;
          }

          .clear-all-btn {
            font-size: 14px;
            padding: 5px 12px;
          }
        }
      `}</style>
    </div>
  );
};

SelectedFilters.propTypes = {
  activeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  className: PropTypes.string,
  onClearAll: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
};

export default SelectedFilters;
